import type { AuthUser } from '@/schemas/auth-user';

import type { AnalyticsConfig } from './config';

/**
 * Injects GTM head script + body noscript.
 * No-ops silently in development; warns and no-ops when gtmContainerId is empty.
 * Deduplicates by checking for an existing element with id="gtm-script".
 */
export function injectGTM(config: AnalyticsConfig): void {
  if (config.environment === 'development') {
    return;
  }

  if (config.gtmContainerId === '') {
    console.warn('[Analytics] GTM container ID is not configured. Skipping GTM injection.');
    return;
  }

  // Deduplicate: bail out if already injected
  if (document.getElementById('gtm-script')) {
    return;
  }

  const containerId = config.gtmContainerId;

  // Inject GTM <script> into <head> using DOM APIs (no innerHTML) to avoid XSS
  const script = document.createElement('script');
  script.id = 'gtm-script';
  // Build the GTM bootstrap inline script using safe DOM text node
  const gtmSnippet = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${containerId}');`;
  script.appendChild(document.createTextNode(gtmSnippet));
  document.head.appendChild(script);

  // Inject GTM <noscript> fallback as first child of <body>
  const noscript = document.createElement('noscript');
  noscript.id = 'gtm-noscript';
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

/**
 * Pushes an event object to window.dataLayer, initializing the array if needed.
 * Wraps the push in a try/catch to prevent analytics errors from surfacing.
 */
export function pushDataLayer(event: Record<string, unknown>): void {
  window.dataLayer = window.dataLayer ?? [];
  try {
    window.dataLayer.push(event);
  } catch (error) {
    console.error('[Analytics] Failed to push event to dataLayer:', error);
  }
}

/**
 * Pushes an anonymized user_identity event to the GTM dataLayer.
 * Silently no-ops in development.
 * Only includes perfil (userRole) and parceiroId (partnerId) — never uuid, nome, apelido, or jwtToken.
 */
export function pushUserIdentity(user: Pick<AuthUser, 'perfil' | 'parceiroId'>, config: AnalyticsConfig): void {
  if (config.environment === 'development') {
    return;
  }

  pushDataLayer({
    event: 'user_identity',
    userRole: user.perfil,
    partnerId: user.parceiroId ?? null,
  });
}
